import {decodeInlineSvg, INLINE_SVG_PREFIX} from "../data/inline-svg";

export type Validator<T> = (value: unknown, path?: string) => T;
export class GridValidationError extends Error {
  constructor(public path: string, message: string) {
    super(`${path}: ${message}`);
    this.name = "GridValidationError";
  }
}
export function fail(path: string, message: string): never {throw new GridValidationError(path, message);}
export const text: Validator<string> = (value, path = "value") => {
  if (typeof value !== "string" || value.length > 10000) fail(path, "Expected string of at most 10000 characters");
  return value;
};
export const finite: Validator<number> = (value, path = "value") => {
  if (typeof value !== "number" || !Number.isFinite(value)) fail(path, "Expected finite number");
  return value;
};
export const boolean: Validator<boolean> = (value, path = "value") => {
  if (typeof value !== "boolean") fail(path, "Expected boolean");
  return value;
};
export function object(value: unknown, path = "value"): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    fail(path, "Expected plain object");
  }
  return value as Record<string, unknown>;
}
export function shape<S extends Record<string, Validator<unknown>>>(fields: S): Validator<{[K in keyof S]: ReturnType<S[K]>}> {
  return (value, path = "value") => {
    const record = object(value, path);
    for (const key of Object.keys(record)) if (!Object.hasOwn(fields, key)) fail(`${path}.${key}`, "Unknown field");
    for (const [key, validate] of Object.entries(fields)) {
      if (!Object.hasOwn(record, key) && !(validate as Validator<unknown> & {optional?: boolean}).optional) fail(`${path}.${key}`, "Missing field");
      validate(record[key], `${path}.${key}`);
    }
    return value as {[K in keyof S]: ReturnType<S[K]>};
  };
}
export function nullable<T>(validate: Validator<T>): Validator<T | null> {
  return (value, path) => value === null ? null : validate(value, path);
}
export function optional<T>(validate: Validator<T>) {
  return Object.assign(((value, path) => value === undefined ? undefined : validate(value, path)) as Validator<T | undefined>, {optional: true});
}
export function array<T>(validate: Validator<T>, {minLength = 0, maxLength = Infinity} = {}): Validator<T[]> {
  return (value, path = "value") => {
    if (!Array.isArray(value)) fail(path, "Expected array");
    if (value.length < minLength || value.length > maxLength) fail(path, `Expected ${minLength}…${maxLength} array entries`);
    value.forEach((entry, i) => validate(entry, `${path}[${i}]`));
    return value as T[];
  };
}
export function dictionary<T>(validate: Validator<T>): Validator<Record<string, T>> {
  return (value, path = "value") => {
    const record = object(value, path);
    for (const [key, entry] of Object.entries(record)) {
      if (["__proto__", "prototype", "constructor"].includes(key)) fail(path, "Unsafe key");
      validate(entry, `${path}.${key}`);
    }
    return value as Record<string, T>;
  };
}
export function literal<T extends string | number>(expected: T): Validator<T> {
  return (value, path = "value") => {if (value !== expected) fail(path, `Expected ${expected}`); return expected;};
}
export function range(min: number, max = Infinity, integer = false): Validator<number> {
  return (value, path = "value") => {
    const number = finite(value, path);
    if (number < min || number > max || (integer && !Number.isInteger(number))) fail(path, `Expected ${integer ? "integer" : "number"} in ${min}…${max}`);
    return number;
  };
}
// Asset ownership/authorization is checked by the persistence API, not this structural validator.
export const assetUrl: Validator<string> = (value, path = "asset") => {
  if (typeof value === "string" && value.startsWith(INLINE_SVG_PREFIX)) {
    try {decodeInlineSvg(value);} catch (error) {fail(path, error instanceof Error ? error.message : "Invalid inline SVG");}
    return value;
  }
  const url = text(value, path);
  if (!url || /[\s\\]/.test(url)) fail(path, "Expected stable asset URL");
  if (/^\/(?!\/)/.test(url)) return url;
  try {
    const parsed = new URL(url);
    const local = parsed.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(parsed.hostname);
    if ((parsed.protocol === "https:" || local) && !parsed.username && !parsed.password) return url;
  } catch { /* Report the same validation error for malformed URLs. */ }
  return fail(path, "Expected HTTPS, loopback HTTP or same-origin asset URL");
};
